import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Briefcase,
} from 'lucide-react';

import { getSourceIcon } from '@/utils/tips/getSourceIcon';
import { getTipPriorityColor } from '@/utils/tips/getTipPriorityColor';
import { getTipPriorityLabel } from '@/utils/tips/getTipPriorityLabel';
import { getTipStatusColor } from '@/utils/tips/getTipStatusColor';
import { formatTimestamp } from '@/utils/formatTimestamp';
import { TipResponse } from '@/types/tips';
import { DownloadAttachmentsDropdown } from './DownloadAttachmentsDropdown';

interface TipsCardProps {
  tip: TipResponse;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onCreateAssignment: (tip: TipResponse) => void;
  onEdit: (tip: TipResponse) => void;
  onDelete: (id: string) => void;
}

export function TipsCard({
  tip,
  onApprove,
  onReject,
  onCreateAssignment,
  onEdit,
  onDelete,
}: TipsCardProps) {
  return (
    <Card className='hover:bg-accent/20 transition-colors'>
      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex-1'>
            <div className='flex items-center gap-2 mb-2'>
              {getSourceIcon(tip.source)}
              <span className='text-sm text-muted-foreground capitalize'>
                {tip.source}
              </span>
              <Badge variant={getTipPriorityColor(tip.priority) as any}>
                {getTipPriorityLabel(tip.priority)}
              </Badge>
              <Badge className={getTipStatusColor(tip.status)}>
                {tip.status.replace('_', ' ')}
              </Badge>
            </div>
            <CardTitle className='text-lg mb-1 text-left'>
              {tip.title}
            </CardTitle>
            {tip.sourceContact && (
              <p className='text-sm text-muted-foreground mb-2 text-left'>
                From: {tip.sourceContact}
              </p>
            )}
          </div>
          <div className='flex gap-3 items-center'>
            <div className='flex items-baseline gap-3'>
              <span className='text-sm text-muted-foreground ml-auto'>
                {formatTimestamp(tip.createdAt)}
              </span>

              <div className='text-right shrink-0 flex gap-1 items-baseline'>
                <div className='text-sm text-muted-foreground'>Priority</div>
                <div className='text-m font-medium text-primary'>
                  {tip.priority}/5
                </div>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='shrink-0'>
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem onClick={() => onEdit(tip)}>
                  <Edit className='mr-2 h-4 w-4' />
                  Edit Tip
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(tip.id)}
                  className='text-destructive'
                >
                  <Trash2 className='mr-2 h-4 w-4' />
                  Delete Tip
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {tip.description && (
          <p className='text-sm mb-4 leading-relaxed text-left'>
            {tip.description}
          </p>
        )}

        {tip.sourceUrl && (
          <div className='mb-4'>
            <a
              href={tip.sourceUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='text-sm text-primary hover:underline'
            >
              View Source →
            </a>
          </div>
        )}

        {tip.tags && (
          <div className='mb-4'>
            <div className='flex flex-wrap gap-1'>
              {tip.tags.split(',').map((tag, index) => (
                <Badge key={index} variant='outline' className='text-xs'>
                  {tag.trim()}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {tip.editorialNotes && (
          <div className='mb-4 p-3 bg-muted rounded-lg'>
            <p className='text-sm font-medium mb-1'>Editorial Notes:</p>
            <p className='text-sm text-muted-foreground'>
              {tip.editorialNotes}
            </p>
          </div>
        )}

        <div className='flex gap-2'>
          {tip.status === 'pending' && (
            <>
              <Button
                size='sm'
                onClick={() => onApprove(tip.id)}
                className='gap-2'
              >
                <CheckCircle className='h-4 w-4' />
                Approve
              </Button>
              <Button
                size='sm'
                variant='outline'
                onClick={() => onReject(tip.id)}
                className='gap-2'
              >
                <XCircle className='h-4 w-4' />
                Reject
              </Button>
            </>
          )}
          {tip.status === 'approved' && (
            <Button
              size='sm'
              onClick={() => onCreateAssignment(tip)}
              className='gap-2'
            >
              <Briefcase className='h-4 w-4' />
              Create Assignment
            </Button>
          )}
          <DownloadAttachmentsDropdown
            attachmentsMetadata={tip.attachmentsMetadata}
            tipId={tip.id}
          />
        </div>
      </CardContent>
    </Card>
  );
}
